import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { getDashboardStats } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  FolderKanban,
  FileSpreadsheet,
  TrendingUp,
  Calculator,
  Plus,
  ArrowRight,
  Landmark
} from 'lucide-react';

const Dashboard = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await getDashboardStats();
      setStats(response.data);
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: t('dashboard.totalProjects'),
      value: stats?.total_projects || 0,
      icon: FolderKanban,
      color: 'from-violet-500 to-indigo-500',
      bgColor: 'bg-violet-50'
    },
    {
      title: t('dashboard.balanceSheets'),
      value: stats?.total_balance_sheets || 0,
      icon: FileSpreadsheet,
      color: 'from-cyan-500 to-teal-500',
      bgColor: 'bg-cyan-50'
    },
    {
      title: t('dashboard.businessPlans'),
      value: stats?.total_business_plans || 0,
      icon: TrendingUp,
      color: 'from-pink-500 to-rose-500',
      bgColor: 'bg-pink-50'
    },
    {
      title: t('dashboard.valuations'),
      value: stats?.total_valuations || 0,
      icon: Calculator,
      color: 'from-amber-500 to-orange-500',
      bgColor: 'bg-amber-50'
    }
  ];

  const quickActions = [
    { label: t('projects.new'), icon: FolderKanban, path: '/projects/new', color: 'bg-violet-100 text-violet-600' },
    { label: t('balance.title'), icon: FileSpreadsheet, path: '/balance-sheet', color: 'bg-cyan-100 text-cyan-600' },
    { label: t('bp.new'), icon: TrendingUp, path: '/business-plan', color: 'bg-pink-100 text-pink-600' },
    { label: t('pf.new'), icon: Landmark, path: '/project-financing', color: 'bg-amber-100 text-amber-600' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fadeIn" data-testid="dashboard">
      {/* Welcome Section */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">
            {t('dashboard.welcome')}, {user?.full_name?.split(' ')[0]}!
          </h1>
          <p className="text-muted-foreground mt-1">
            Ecco un riepilogo delle tue attività
          </p>
        </div>
        <Link to="/projects/new">
          <Button className="bg-gradient-to-r from-violet-600 to-indigo-600 hover:opacity-90 shadow-lg rounded-full px-6" data-testid="new-project-btn">
            <Plus size={18} className="mr-2" />
            {t('projects.new')}
          </Button>
        </Link>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 stagger-children">
        {statCards.map((stat, index) => (
          <Card key={index} className="border-0 shadow-lg card-hover bg-white" data-testid={`stat-card-${index}`}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">{stat.title}</p>
                  <p className="text-3xl font-bold mt-2 number-format">{stat.value}</p>
                </div>
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-r ${stat.color} flex items-center justify-center shadow-lg`}>
                  <stat.icon size={24} className="text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Recent Projects */}
        <Card className="lg:col-span-2 border-0 shadow-lg" data-testid="recent-projects">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-xl">{t('dashboard.recentProjects')}</CardTitle>
              <Link to="/projects">
                <Button variant="ghost" size="sm" className="text-primary">
                  Vedi tutti <ArrowRight size={16} className="ml-1" />
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {stats?.recent_projects?.length > 0 ? (
              <div className="space-y-4">
                {stats.recent_projects.map((project, index) => (
                  <Link 
                    key={project.id} 
                    to={`/projects/${project.id}`}
                    className="block"
                    data-testid={`recent-project-${index}`}
                  >
                    <div className="flex items-center justify-between p-4 rounded-xl border border-border/50 hover:border-primary/50 hover:bg-muted/30 transition-all">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                          <FolderKanban size={20} className="text-primary" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{project.name}</p>
                          <p className="text-sm text-muted-foreground">{project.company_name}</p>
                        </div>
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {project.fiscal_year}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <FolderKanban size={48} className="mx-auto mb-4 opacity-30" />
                <p>{t('common.noData')}</p>
                <Link to="/projects/new">
                  <Button variant="outline" className="mt-4" data-testid="create-first-project">
                    <Plus size={16} className="mr-2" />
                    Crea il tuo primo progetto
                  </Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card className="border-0 shadow-lg" data-testid="quick-actions">
          <CardHeader className="pb-4">
            <CardTitle className="text-xl">{t('dashboard.quickActions')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {quickActions.map((action, index) => (
                <Link key={index} to={action.path} data-testid={`quick-action-${index}`}>
                  <div className="flex items-center gap-4 p-4 rounded-xl border border-border/50 hover:border-primary/50 hover:bg-muted/30 transition-all cursor-pointer">
                    <div className={`w-10 h-10 rounded-lg ${action.color} flex items-center justify-center`}>
                      <action.icon size={20} />
                    </div>
                    <span className="font-medium">{action.label}</span>
                    <ArrowRight size={16} className="ml-auto text-muted-foreground" />
                  </div>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
